import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// The privacy-policy tests render this component with tsx, which has no root tsconfig and would fall back to the classic
// JSX runtime (`React.createElement`); pin the automatic runtime here (tsc's jsx: react-jsx agrees).
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import BarChart3 from 'lucide-react/dist/esm/icons/chart-column.mjs';
import Building2 from 'lucide-react/dist/esm/icons/building-complex.mjs';
import Check from 'lucide-react/dist/esm/icons/check.mjs';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.mjs';
import CircleHelp from 'lucide-react/dist/esm/icons/circle-question-mark.mjs';
import Globe from 'lucide-react/dist/esm/icons/globe.mjs';
import Info from 'lucide-react/dist/esm/icons/info.mjs';
import Monitor from 'lucide-react/dist/esm/icons/monitor.mjs';
import Plug from 'lucide-react/dist/esm/icons/plug.mjs';
import Settings from 'lucide-react/dist/esm/icons/settings.mjs';
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check.mjs';
import UserRound from 'lucide-react/dist/esm/icons/user-round.mjs';
import { placePopover, popoverBounds, popoverStyle } from './popoverPlacement.js';
/** The menu's width where there is room (gyro.css gives the same as its fallback where nothing is laid out). */
export const ACCOUNT_MENU_WIDTH = 314;
/**
 * The account menu from the avatar: in the sidebar's footer (opens above its button) or in the header (opens under it).
 *
 * The header menu is rendered into the widget's panel, not beside its button: the main surface clips its overflow, and a
 * 314 px menu whose right edge sits on a 29 px button a third of the way across the header ran past that surface's left
 * edge (owner screenshot, 2026-10-17: no icons, the first letters cut off). Both placements are measured and kept inside
 * the window with an 8 px gutter (popoverPlacement.ts), shrinking on a narrow pane; keyboard, Escape and a press outside
 * work as before.
 */
export function DesktopAccountMenu({ name, email, avatar, placement = 'footer', onOpenSettings, onOpenGuide }) {
    const [open, setOpen] = useState(false);
    const [portalTarget, setPortalTarget] = useState(null);
    const [style, setStyle] = useState(undefined);
    const root = useRef(null);
    const trigger = useRef(null);
    const menu = useRef(null);
    const id = useId();
    const header = placement === 'header';
    const close = () => { setOpen(false); trigger.current?.focus(); };
    // The header menu leaves the clipped main surface for the panel (decided as it opens, so it mounts there once and keeps
    // the focus the effect below gives it); the footer menu stays in the sidebar, its own stacking context.
    const toggle = () => {
        setPortalTarget(header ? root.current?.closest('.gyro-panel') ?? null : null);
        setOpen(value => !value);
    };
    useLayoutEffect(() => {
        if (!open) {
            setStyle(undefined);
            return;
        }
        const measure = () => {
            const button = trigger.current;
            const popup = menu.current;
            const container = portalTarget ?? root.current;
            const view = button?.ownerDocument.defaultView;
            if (!button || !popup || !container || !view)
                return;
            const anchor = button.getBoundingClientRect();
            // No window to measure against (a server render): the stylesheet's place stands.
            if (!(view.innerWidth > 0))
                return;
            const placed = placePopover({
                anchor,
                bounds: popoverBounds(view, root.current?.closest('.gyro-panel')),
                width: ACCOUNT_MENU_WIDTH,
                height: popup.getBoundingClientRect().height,
                align: header ? 'end' : 'start',
                side: header ? 'below' : 'above',
                gap: header ? 12 : 10,
            });
            const next = popoverStyle(placed, container);
            setStyle((previous) => previous && Object.keys(next).every((key) => previous[key] === next[key]) ? previous : next);
        };
        measure();
        const view = trigger.current?.ownerDocument.defaultView;
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : undefined;
        if (trigger.current)
            observer?.observe(trigger.current);
        if (menu.current)
            observer?.observe(menu.current);
        view?.addEventListener('resize', measure);
        view?.addEventListener('scroll', measure, true);
        view?.visualViewport?.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            view?.removeEventListener('resize', measure);
            view?.removeEventListener('scroll', measure, true);
            view?.visualViewport?.removeEventListener('resize', measure);
        };
    }, [open, header, portalTarget]);
    useEffect(() => {
        if (!open)
            return;
        menu.current?.querySelector('[role="menuitem"]')?.focus();
        // (The menu is mounted where it stays, so this runs once per opening.)
        const outside = (event) => {
            if (event.target instanceof Node && !root.current?.contains(event.target) && !menu.current?.contains(event.target))
                setOpen(false);
        };
        document.addEventListener('pointerdown', outside);
        return () => document.removeEventListener('pointerdown', outside);
    }, [open]);
    const select = (section) => {
        setOpen(false);
        onOpenSettings(section, trigger.current ?? undefined);
    };
    const keydown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            close();
            return;
        }
        if (event.key === 'Tab') {
            event.preventDefault();
            event.stopPropagation();
            close();
            return;
        }
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key))
            return;
        event.preventDefault();
        const items = Array.from(menu.current?.querySelectorAll('[role="menuitem"]') ?? []);
        const current = items.indexOf(document.activeElement);
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
            : (current + (event.key === 'ArrowUp' ? -1 : 1) + items.length) % items.length;
        items[index]?.focus();
    };
    const panel = open && _jsxs("div", { ref: menu, id: id, className: `gyro-account-menu${header ? ' is-header' : ''}`, role: "menu", "aria-label": "Account menu", style: style, onKeyDown: keydown, children: [_jsx("div", { className: "gyro-account-identity", children: email || name }), _jsxs("button", { className: "gyro-account-workspace", role: "menuitem", onClick: () => select('organization'), children: [_jsx("img", { src: avatar, alt: "" }), _jsxs("span", { children: [_jsx("strong", { children: "Exante360" }), _jsx("small", { children: "Organization" })] }), _jsx(Check, { size: 17 })] }), _jsxs("button", { className: "gyro-account-menu-item", role: "menuitem", onClick: () => select('account'), children: [_jsx(UserRound, { size: 18 }), _jsx("span", { children: "Your account" })] }), _jsx("div", { className: "gyro-account-divider", role: "separator" }), _jsxs("button", { className: "gyro-account-menu-item", role: "menuitem", onClick: () => select('general'), children: [_jsx(Settings, { size: 18 }), _jsx("span", { children: "Settings" }), _jsx("kbd", { className: "gyro-account-shortcut", children: "Ctrl+," })] }), _jsxs("button", { className: "gyro-account-menu-item", role: "menuitem", onClick: () => select('organization'), children: [_jsx(Building2, { size: 18 }), _jsx("span", { children: "Organization settings" })] }), _jsxs("button", { className: "gyro-account-menu-item", role: "menuitem", onClick: () => select('usage'), children: [_jsx(BarChart3, { size: 18 }), _jsx("span", { children: "Usage" })] }), _jsxs("button", { className: "gyro-account-menu-item", role: "menuitem", onClick: () => select('general'), children: [_jsx(Globe, { size: 18 }), _jsx("span", { children: "Language & appearance" })] }), _jsx("div", { className: "gyro-account-divider", role: "separator" }), _jsxs("button", { className: "gyro-account-menu-item", role: "menuitem", onClick: () => select('system'), children: [_jsx(Monitor, { size: 18 }), _jsx("span", { children: "Desktop app" })] }), _jsxs("button", { className: "gyro-account-menu-item", role: "menuitem", onClick: () => select('extensions'), children: [_jsx(Plug, { size: 18 }), _jsx("span", { children: "Extensions" })] }), onOpenGuide && _jsxs("button", { className: "gyro-account-menu-item", role: "menuitem", onClick: () => { close(); onOpenGuide(); }, children: [_jsx(CircleHelp, { size: 18 }), _jsx("span", { children: "User guide" })] }), _jsxs("button", { className: "gyro-account-menu-item", role: "menuitem", onClick: () => select('system'), children: [_jsx(CircleHelp, { size: 18 }), _jsx("span", { children: "Help & shortcuts" })] }), _jsx("div", { className: "gyro-account-divider", role: "separator" }), _jsxs("button", { className: "gyro-account-menu-item", role: "menuitem", onClick: () => select('privacy-policy'), children: [_jsx(ShieldCheck, { size: 18 }), _jsx("span", { children: "Privacy policy" })] }), _jsxs("button", { className: "gyro-account-menu-item", role: "menuitem", onClick: () => select('developer'), children: [_jsx(Info, { size: 18 }), _jsx("span", { children: "About Gyro" })] })] });
    return _jsxs("div", { ref: root, className: `gyro-account-footer${header ? ' is-header' : ''}`, children: [_jsxs("button", { ref: trigger, className: `gyro-account-trigger${header ? ' gyro-account-header-button' : ''}`, "aria-label": "Account and settings", "aria-haspopup": "menu", "aria-expanded": open, "aria-controls": open ? id : undefined, onClick: toggle, children: [_jsx("img", { className: "gyro-account-avatar", src: avatar, alt: "" }), placement === 'footer' && _jsxs(_Fragment, { children: [_jsxs("span", { className: "gyro-account-copy", children: [_jsx("span", { className: "gyro-account-name", children: name }), _jsx("span", { className: "gyro-account-org", children: "Exante360" })] }), _jsx(ChevronDown, { size: 15 })] })] }), panel && (portalTarget ? createPortal(panel, portalTarget) : panel)] });
}
//# sourceMappingURL=DesktopAccountMenu.js.map